'use client';

import { useState, useCallback, useRef } from 'react';
import { 
  ArrowLeft, 
  Sparkles, 
  Edit2, 
  Copy, 
  FileText,
  ExternalLink,
  Save,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { RichTextEditor } from '@/components/shared/lexical-editor';
import { AskAIBar } from '@/components/ai';
import { RelatedArtifactsPanel } from './related-artifacts-panel';
import {
  PersonaTemplate,
  UseCaseTemplate,
  JourneyTemplate,
  ServiceMapTemplate,
  StoryboardTemplate,
} from './templates';
import {
  type DefinitionPersona,
  type DefinitionUseCase,
  type DefinitionJourney,
  type DefinitionServiceMap,
  type DefinitionStoryboard,
  type DefinitionAttachment,
} from '@/hooks/use-definition';

type DefinitionItem = DefinitionPersona | DefinitionUseCase | DefinitionJourney | DefinitionServiceMap | DefinitionStoryboard;

interface DefinitionDetailViewProps {
  item: DefinitionItem;
  type: 'personas' | 'use-cases' | 'journeys' | 'service-maps' | 'storyboards';
  projectId: string;
  projectName?: string;
  onBack: () => void;
  onEdit: () => void;
  onSave?: (data: Partial<DefinitionPersona> | Partial<DefinitionUseCase>) => Promise<void>;
  onAskAI?: (message: string) => Promise<void>;
}

const TYPE_TO_ARTIFACT_TYPE: Record<string, string> = {
  'personas': 'PERSONA',
  'use-cases': 'USE_CASE',
  'journeys': 'JOURNEY',
  'service-maps': 'SERVICE_MAP',
  'storyboards': 'STORYBOARD',
};

/* ─── Lexical HTML builders (edit mode only) ─── */

function buildPersonaHtml(persona: DefinitionPersona): string {
  const parts: string[] = [];
  parts.push(`<h1>${persona.name || 'Unnamed Persona'}</h1>`);
  if (persona.role) parts.push(`<p>${persona.role}</p>`);
  if (persona.demographics) parts.push(`<h2>Overview</h2><p>${persona.demographics.replace(/\n/g, '<br/>')}</p>`);
  if (persona.contexts) parts.push(`<h2>Background</h2><p>${persona.contexts.replace(/\n/g, '<br/>')}</p>`);
  if (persona.primaryGoals) parts.push(`<h2>Goals &amp; Objectives</h2><p>${persona.primaryGoals.replace(/\n/g, '<br/>')}</p>`);
  if (persona.tools) parts.push(`<h2>Key Behaviors</h2><p>${persona.tools.replace(/\n/g, '<br/>')}</p>`);
  if (persona.motivations) parts.push(`<h2>Motivations</h2><p>${persona.motivations.replace(/\n/g, '<br/>')}</p>`);
  if (persona.challenges) parts.push(`<h2>Frustrations &amp; Pain Points</h2><p>${persona.challenges.replace(/\n/g, '<br/>')}</p>`);
  if (persona.accessibilityNeeds) parts.push(`<h2>Accessibility</h2><p>${persona.accessibilityNeeds.replace(/\n/g, '<br/>')}</p>`);
  if (persona.quotes) parts.push(`<h2>Notable Quotes</h2><p>${persona.quotes.replace(/\n/g, '<br/>')}</p>`);
  return parts.join('\n');
}

function parsePersonaHtml(html: string): { fields: Partial<DefinitionPersona>; html: string } {
  const div = document.createElement('div');
  div.innerHTML = html;
  const text = (el: Element | null): string => el?.textContent?.trim() || '';

  const h1 = div.querySelector('h1');
  const fields: Partial<DefinitionPersona> = { name: text(h1) };

  const h2s = div.querySelectorAll('h2');
  h2s.forEach((h2) => {
    const sectionName = text(h2).toLowerCase();
    const contentParts: string[] = [];
    let sibling = h2.nextElementSibling;
    while (sibling && sibling.tagName !== 'H2' && sibling.tagName !== 'H1') {
      contentParts.push(sibling.textContent?.trim() || '');
      sibling = sibling.nextElementSibling;
    }
    const content = contentParts.filter(Boolean).join('\n');

    if (sectionName.includes('overview')) fields.demographics = content;
    else if (sectionName.includes('background')) fields.contexts = content;
    else if (sectionName.includes('goal')) fields.primaryGoals = content;
    else if (sectionName.includes('behavior')) fields.tools = content;
    else if (sectionName.includes('motivation')) fields.motivations = content;
    else if (sectionName.includes('frustration') || sectionName.includes('pain')) fields.challenges = content;
    else if (sectionName.includes('accessibility')) fields.accessibilityNeeds = content;
    else if (sectionName.includes('quote')) fields.quotes = content;
  });

  if (h1?.nextElementSibling?.tagName === 'P') {
    const firstP = h1.nextElementSibling;
    const txt = text(firstP);
    if (!txt.startsWith('✓')) {
      fields.role = txt;
    }
  }

  return { fields, html };
}

function buildUseCaseHtml(uc: DefinitionUseCase): string {
  const parts: string[] = [];
  parts.push(`<h1>${uc.title || 'Untitled Use Case'}</h1>`);
  if (uc.description) parts.push(`<h2>Description</h2><p>${uc.description.replace(/\n/g, '<br/>')}</p>`);
  if (uc.preconditions) parts.push(`<h2>Preconditions</h2><p>${uc.preconditions.replace(/\n/g, '<br/>')}</p>`);
  if (Array.isArray(uc.mainFlow) && uc.mainFlow.length > 0) {
    parts.push('<h2>Main Flow</h2><ol>');
    uc.mainFlow.forEach((step) => {
      const stepText = typeof step === 'string'
        ? step
        : (step as { action?: string; description?: string })?.action
          || (step as { description?: string })?.description
          || String(step);
      parts.push(`<li>${stepText}</li>`);
    });
    parts.push('</ol>');
  }
  if (Array.isArray(uc.exceptionFlows) && uc.exceptionFlows.length > 0) {
    parts.push('<h2>Exception Handling</h2><ul>');
    uc.exceptionFlows.forEach((flow) => {
      const flowText = typeof flow === 'string'
        ? flow
        : (flow as { trigger?: string; name?: string })?.trigger
          || (flow as { name?: string })?.name
          || String(flow);
      parts.push(`<li>${flowText}</li>`);
    });
    parts.push('</ul>');
  }
  if (uc.postconditions) parts.push(`<h2>Postconditions</h2><p>${uc.postconditions.replace(/\n/g, '<br/>')}</p>`);
  if (uc.businessRules) parts.push(`<h2>Business Rules</h2><p>${uc.businessRules.replace(/\n/g, '<br/>')}</p>`);
  return parts.join('\n');
}

function parseUseCaseHtml(html: string): { fields: Partial<DefinitionUseCase>; html: string } {
  const div = document.createElement('div');
  div.innerHTML = html;
  const text = (el: Element | null): string => el?.textContent?.trim() || '';

  const h1 = div.querySelector('h1');
  const fields: Partial<DefinitionUseCase> = { title: text(h1) };

  const h2s = div.querySelectorAll('h2');
  h2s.forEach((h2) => {
    const sectionName = text(h2).toLowerCase();
    const contentParts: string[] = [];
    let sibling = h2.nextElementSibling;
    while (sibling && sibling.tagName !== 'H2' && sibling.tagName !== 'H1') {
      if (sibling.tagName === 'OL' || sibling.tagName === 'UL') {
        const items = sibling.querySelectorAll('li');
        items.forEach(li => contentParts.push(li.textContent?.trim() || ''));
      } else {
        contentParts.push(sibling.textContent?.trim() || '');
      }
      sibling = sibling.nextElementSibling;
    }
    const content = contentParts.filter(Boolean).join('\n');

    if (sectionName.includes('description')) fields.description = content;
    else if (sectionName.includes('precondition')) fields.preconditions = content;
    else if (sectionName.includes('main flow')) fields.mainFlow = contentParts.filter(Boolean);
    else if (sectionName.includes('exception')) fields.exceptionFlows = contentParts.filter(Boolean);
    else if (sectionName.includes('postcondition')) fields.postconditions = content;
    else if (sectionName.includes('business')) fields.businessRules = content;
  });

  return { fields, html };
}

/* ─── Shared sub-components ─── */

function AttachmentsSection({ attachments }: { attachments: DefinitionAttachment[] }) {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      <div className="flex items-center gap-2 mb-4">
        <FileText className="w-4 h-4 text-gray-400" />
        <span className="font-medium text-gray-900">Sources & References</span>
        <span className="text-gray-400">({attachments.length})</span>
      </div>
      <div className="space-y-3">
        {attachments.map((attachment) => (
          <div key={attachment.id} className="flex items-start gap-3">
            <ExternalLink className="w-4 h-4 text-gray-400 mt-1" />
            <div>
              <div className="flex items-center gap-2">
                <span className="text-gray-900">{attachment.fileName}</span>
                <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                  {attachment.fileType.split('/')[1]?.toUpperCase() || 'File'}
                </span>
              </div>
              <div className="text-sm text-gray-500">
                {new Date(attachment.uploadedAt).toLocaleDateString('en-US', { 
                  month: 'short', day: 'numeric', year: 'numeric' 
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Main Component ─── */

export function DefinitionDetailView({
  item,
  type,
  projectId,
  projectName = 'Project',
  onBack,
  onEdit,
  onSave,
  onAskAI,
}: DefinitionDetailViewProps) {
  const [showAskAI, setShowAskAI] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedHtml, setEditedHtml] = useState('');
  const [savedData, setSavedData] = useState<Partial<DefinitionPersona> | Partial<DefinitionUseCase> | null>(null);
  const [savedHtml, setSavedHtml] = useState('');
  const initialEditorHtmlRef = useRef('');
  const handleEditorChange = useCallback((html: string) => {
    if (!initialEditorHtmlRef.current) {
      initialEditorHtmlRef.current = html;
    }
    setEditedHtml(html);
  }, []);

  const hasChanges = (): boolean => isEditing && editedHtml !== '' && editedHtml !== initialEditorHtmlRef.current;

  const handleSaveChanges = async () => {
    if (!onSave || !editedHtml) return;
    setIsSaving(true);
    try {
      let fields: Partial<DefinitionPersona> | Partial<DefinitionUseCase>;
      let html: string;
      if (type === 'use-cases') {
        const result = parseUseCaseHtml(editedHtml);
        fields = result.fields;
        html = result.html;
      } else {
        const result = parsePersonaHtml(editedHtml);
        fields = result.fields;
        html = result.html;
      }
      await onSave(fields);
      setSavedData(fields);
      setSavedHtml(html);
      setIsEditing(false);
      setEditedHtml('');
      initialEditorHtmlRef.current = '';
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedHtml('');
    initialEditorHtmlRef.current = '';
  };

  const getBackLabel = () => {
    switch (type) {
      case 'personas': return 'Back to Personas';
      case 'use-cases': return 'Back to Use Cases';
      case 'journeys': return 'Back to User Journeys';
      case 'service-maps': return 'Back to Service Maps';
      case 'storyboards': return 'Back to Storyboards';
    }
  };

  const handleAskAI = async (message: string) => {
    if (onAskAI) {
      await onAskAI(message);
    }
    return { response: '', citations: [] };
  };

  const getItemName = () => {
    if (savedData && type === 'personas' && (savedData as Partial<DefinitionPersona>).name) {
      return (savedData as Partial<DefinitionPersona>).name;
    }
    if (savedData && type === 'use-cases' && (savedData as Partial<DefinitionUseCase>).title) {
      return (savedData as Partial<DefinitionUseCase>).title;
    }
    switch (type) {
      case 'personas': return (item as DefinitionPersona).name;
      case 'use-cases': return (item as DefinitionUseCase).title;
      case 'journeys': return (item as DefinitionJourney).scenario;
      case 'service-maps': return (item as DefinitionServiceMap).title;
      case 'storyboards': return (item as DefinitionStoryboard).scenario;
    }
  };

  const getItemSubtitle = () => {
    switch (type) {
      case 'personas': return (item as DefinitionPersona).role;
      case 'use-cases': {
        const uc = item as DefinitionUseCase;
        return `${uc.actor || 'No Actor'} - ${uc.priority || 'No Priority'} Priority`;
      }
      case 'journeys': return (item as DefinitionJourney).description;
      case 'service-maps': return (item as DefinitionServiceMap).description;
      case 'storyboards': return (item as DefinitionStoryboard).description;
    }
  };

  const renderEditableContent = () => {
    const editable = type === 'personas' || type === 'use-cases';
    if (!editable) return null;

    let editorContent: string;
    if (type === 'personas') {
      const persona = item as DefinitionPersona;
      const dp = savedData ? { ...persona, ...savedData } as DefinitionPersona : persona;
      editorContent = savedHtml || buildPersonaHtml(dp);
    } else {
      const useCase = item as DefinitionUseCase;
      const duc = savedData ? { ...useCase, ...savedData } as DefinitionUseCase : useCase;
      editorContent = savedHtml || buildUseCaseHtml(duc);
    }

    return (
      <div className={cn(
        "bg-white rounded-xl mb-6 transition-all overflow-hidden",
        "border-2 border-purple-400 shadow-[0_0_0_4px_rgba(147,51,234,0.1)]"
      )}>
        <RichTextEditor
          key="editing"
          initialContent={editorContent}
          onChange={handleEditorChange}
          placeholder={type === 'personas' ? 'Edit persona content...' : 'Edit use case content...'}
          editable
          minHeight="150px"
        />
      </div>
    );
  };

  const renderContent = () => {
    if (isEditing && (type === 'personas' || type === 'use-cases')) {
      return renderEditableContent();
    }

    const attachments = (item as DefinitionPersona).attachments;

    switch (type) {
      case 'personas': {
        const persona = item as DefinitionPersona;
        const dp = savedData ? { ...persona, ...savedData } as DefinitionPersona : persona;
        return (
          <>
            <PersonaTemplate persona={dp} />
            <AttachmentsSection attachments={attachments} />
          </>
        );
      }
      case 'use-cases': {
        const useCase = item as DefinitionUseCase;
        const duc = savedData ? { ...useCase, ...savedData } as DefinitionUseCase : useCase;
        return (
          <>
            <UseCaseTemplate useCase={duc} />
            <AttachmentsSection attachments={attachments} />
          </>
        );
      }
      case 'journeys':
        return <JourneyTemplate journey={item as DefinitionJourney} />;
      case 'service-maps':
        return <ServiceMapTemplate serviceMap={item as DefinitionServiceMap} />;
      case 'storyboards':
        return <StoryboardTemplate storyboard={item as DefinitionStoryboard} />;
    }
  };

  return (
    <div className="flex flex-col h-full w-full">
      {/* FIXED HEADER */}
      <div className="shrink-0 w-full">
        {/* Row 1: Back + Edit/Save */}
        <div className="w-full bg-white border-b border-gray-200">
          <div className="flex items-center justify-between px-8 py-4">
            <button 
              onClick={() => {
                if (isEditing) {
                  handleCancelEdit();
                  return;
                }
                onBack();
              }}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{isEditing ? 'Back' : getBackLabel()}</span>
            </button>
            {(type === 'personas' || type === 'use-cases') && (
              isEditing ? (
                <button 
                  onClick={handleSaveChanges}
                  disabled={isSaving || !hasChanges()}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 rounded-lg transition-colors",
                    hasChanges() 
                      ? "text-white bg-purple-600 hover:bg-purple-700" 
                      : "text-gray-400 bg-gray-200 cursor-not-allowed",
                    isSaving && "opacity-50"
                  )}
                >
                  <Save className="w-4 h-4" />
                  <span>{isSaving ? 'Saving...' : 'Save Changes'}</span>
                </button>
              ) : (
                <button 
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                  <span>Edit</span>
                </button>
              )
            )}
          </div>
        </div>

        {/* Row 2: Name/Subtitle + Action Buttons */}
        <div className="w-full bg-white border-b border-gray-200">
          <div className="flex items-start justify-between px-8 py-4 gap-4">
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold text-gray-900 truncate">{getItemName()}</h1>
              <p className="text-gray-600">{getItemSubtitle()}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setShowAskAI(!showAskAI)}
                className="h-[42px] px-4 flex items-center gap-2 rounded-full transition-colors text-white bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 whitespace-nowrap"
              >
                <Sparkles className="w-4 h-4 shrink-0" />
                <span className="text-sm font-medium">Ask AI</span>
              </button>
              <button className="h-[42px] px-4 flex items-center gap-2 border border-gray-300 rounded-full hover:bg-gray-50 transition-colors text-gray-700 whitespace-nowrap">
                <Copy className="w-4 h-4 shrink-0" />
                <span className="text-sm font-medium">Copy</span>
              </button>
            </div>
          </div>
        </div>

        {/* Ask AI Bar */}
        {showAskAI && (
          <div className="w-full bg-gray-50 border-b border-gray-200 py-4">
            <div className="px-8">
              <AskAIBar
                isOpen={showAskAI}
                onClose={() => setShowAskAI(false)}
                onSend={handleAskAI}
                placeholder="Ask AI about this item..."
              />
            </div>
          </div>
        )}
      </div>

      {/* SCROLLABLE CONTENT */}
      <div className="flex-1 min-h-0 overflow-y-auto w-full bg-gray-50">
        <div className="container px-8 py-6 space-y-6">
          {renderContent()}

          <RelatedArtifactsPanel
            projectId={projectId}
            artifactType={TYPE_TO_ARTIFACT_TYPE[type] || 'PERSONA'}
            artifactId={item.id}
          />
        </div>
      </div>
    </div>
  );
}
